import { AxiosRequestConfig } from "axios";
import { CohesiveAPIClient } from "../../client";
import {
  ArchivedFlowSessionListResponse,
  CreateFlowSessionRequest,
  FlowSessionResponse,
  FlowSessionsResponse,
  SetFlowSessionNameRequest,
  UpdateFlowSessionReadStatusPayload,
  UpdateFlowSessionRequest,
} from "./types";

import { route } from "../../utils";

export * from "./types";

const ENDPOINTS = {
  LIST: "/api/v2/org/:organization_id/user/:user_id/flow-sessions",
  SHOW: "/api/v2/org/:organization_id/user/:user_id/flow-sessions/:flow_session_id",
  CREATE: "/api/v2/org/:organization_id/user/:user_id/flow-sessions",
  UPDATE:
    "/api/v2/org/:organization_id/user/:user_id/flow-sessions/:flow_session_id",
  DELETE:
    "/api/v2/org/:organization_id/user/:user_id/flow-sessions/:flow_session_id",
  RESTORE:
    "/api/v2/org/:organization_id/user/:user_id/flow-sessions/:flow_session_id/restore",
  ARCHIVED: "/api/v2/org/:organization_id/user/:user_id/archived/flow-sessions",
  CANCEL:
    "/api/v2/org/:organization_id/user/:user_id/flow-sessions/:flow_session_id/cancel",
  UPDATE_READ_STATUS:
    "/api/v2/org/:organization_id/user/:user_id/flow-sessions/:flow_session_id/read-status",
  SET_FOLDER:
    "/api/v2/org/:organization_id/user/:user_id/flow-sessions/:flow_session_id/folder",
};

export class FlowSessionsAPI {
  constructor(private client: CohesiveAPIClient) {}

  async list(config?: AxiosRequestConfig): Promise<FlowSessionsResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<FlowSessionsResponse>(
      route(ENDPOINTS.LIST, {
        organization_id: organizationId,
        user_id: userId,
      }),
      config,
    );
    return response.data;
  }

  async getById(
    flowSessionId: string,
    config?: AxiosRequestConfig,
  ): Promise<FlowSessionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.get<FlowSessionResponse>(
      route(ENDPOINTS.SHOW, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      config,
    );
    return response.data;
  }

  async create(
    data: CreateFlowSessionRequest,
    config?: AxiosRequestConfig,
  ): Promise<FlowSessionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.post<
      FlowSessionResponse,
      any,
      CreateFlowSessionRequest
    >(
      route(ENDPOINTS.CREATE, {
        organization_id: organizationId,
        user_id: userId,
      }),
      data,
      config,
    );
    return response.data;
  }

  async updateName(
    flowSessionId: string,
    data: SetFlowSessionNameRequest,
    config?: AxiosRequestConfig,
  ): Promise<FlowSessionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.put<
      FlowSessionResponse,
      any,
      SetFlowSessionNameRequest
    >(
      route(ENDPOINTS.UPDATE, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      data,
      config,
    );
    return response.data;
  }

  /**
   * Move a chat into a folder, or to the workspace root when `folderId` is null.
   * The destination folder must be a chat folder in the chat's own workspace.
   */
  async setFolder(
    flowSessionId: string,
    folderId: string | null,
    config?: AxiosRequestConfig,
  ): Promise<FlowSessionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.put<FlowSessionResponse>(
      route(ENDPOINTS.SET_FOLDER, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      { folder_id: folderId },
      config,
    );
    return response.data;
  }

  async update(
    flowSessionId: string,
    data: UpdateFlowSessionRequest,
    config?: AxiosRequestConfig,
  ): Promise<FlowSessionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.put<
      FlowSessionResponse,
      any,
      UpdateFlowSessionRequest
    >(
      route(ENDPOINTS.UPDATE, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      data,
      config,
    );
    return response.data;
  }

  async updateReadStatus(
    flowSessionId: string,
    data: UpdateFlowSessionReadStatusPayload,
    config?: AxiosRequestConfig,
  ): Promise<FlowSessionResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response = await this.client.axios.put<
      FlowSessionResponse,
      any,
      UpdateFlowSessionReadStatusPayload
    >(
      route(ENDPOINTS.UPDATE_READ_STATUS, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      data,
      config,
    );
    return response.data;
  }

  async delete(
    flowSessionId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    await this.client.axios.delete(
      route(ENDPOINTS.DELETE, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      config,
    );
  }

  /** Restores a chat from the archive; rejected if its parent canvas/workspace is archived. */
  async restore(
    flowSessionId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    await this.client.axios.post(
      route(ENDPOINTS.RESTORE, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      {},
      config,
    );
  }

  /** Lists the user's archived chats, newest first (cursor-paginated by archived_at). */
  async listArchived(
    params?: { limit?: number; before_ts?: string },
    config?: AxiosRequestConfig,
  ): Promise<ArchivedFlowSessionListResponse> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    const response =
      await this.client.axios.get<ArchivedFlowSessionListResponse>(
        route(ENDPOINTS.ARCHIVED, {
          organization_id: organizationId,
          user_id: userId,
        }),
        { ...config, params: { ...config?.params, ...params } },
      );
    return response.data;
  }

  async cancel(
    flowSessionId: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    const organizationId = await this.client.getOrgID();
    const userId = await this.client.getUserID();
    await this.client.axios.put(
      route(ENDPOINTS.CANCEL, {
        organization_id: organizationId,
        user_id: userId,
        flow_session_id: flowSessionId,
      }),
      config,
    );
  }
}
